import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "@/components/ui/button";
import { RefreshCcw, Navigation, Route } from "lucide-react";

// Custom pin icon
const makeIcon = (n) =>
  L.divIcon({
    className: "",
    html: `<div style="background:#C85A17;color:white;width:34px;height:34px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:grid;place-content:center;box-shadow:0 4px 8px rgba(0,0,0,.25);border:2px solid white;">
             <div style="transform:rotate(45deg);font-family:'Fraunces',serif;font-weight:800;font-size:14px;line-height:1;">${n}</div>
           </div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 34],
  });

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 13);
    } else {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [points, map]);
  return null;
}

function isToday(iso) {
  const d = new Date(iso), t = new Date();
  return d.toDateString() === t.toDateString();
}

// Haversine distance in km
function distanceKm(a, b) {
  const R = 6371, toR = (x) => x * Math.PI / 180;
  const dLat = toR(b[0] - a[0]), dLng = toR(b[1] - a[1]);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toR(a[0])) * Math.cos(toR(b[0])) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

export default function MapView() {
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geocoding, setGeocoding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/appointments");
      setAppts(data);
    } catch { toast.error("Failed to load appointments"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runGeocode = async () => {
    setGeocoding(true);
    try {
      const { data } = await api.post("/appointments/geocode");
      toast.success(`Located ${data.updated} of ${data.total} stops`);
      await load();
    } catch (e) { toast.error("Geocode failed"); }
    finally { setGeocoding(false); }
  };

  const todays = appts
    .filter(a => isToday(a.start) && a.status !== "cancelled")
    .sort((a, b) => (a.route_order ?? 0) - (b.route_order ?? 0) || new Date(a.start) - new Date(b.start));

  const points = todays
    .filter(a => a.latitude && a.longitude)
    .map(a => [a.latitude, a.longitude]);

  // Estimated drive time: assume 40 km/h city speed
  const segments = [];
  for (let i = 1; i < points.length; i++) {
    const km = distanceKm(points[i - 1], points[i]);
    const min = Math.round((km / 40) * 60);
    segments.push({ from: i - 1, to: i, km, min });
  }
  const totalMin = segments.reduce((s, x) => s + x.min, 0);
  const totalKm = segments.reduce((s, x) => s + x.km, 0);

  const missing = todays.length - points.length;

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-accent">Van view</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">Route map</h1>
          <p className="text-sm text-[#6B625B] mt-1">
            Today's {todays.length} stop{todays.length === 1 ? "" : "s"}
            {points.length > 1 && ` · ${totalKm.toFixed(1)} km · ~${totalMin} min driving`}
          </p>
        </div>
        <Button onClick={runGeocode} disabled={geocoding}
          className="rounded-full bg-[#2D4A3E] hover:bg-[#21382E] btn-tactile"
          data-testid="btn-geocode">
          <RefreshCcw className={`w-4 h-4 mr-1 ${geocoding ? "animate-spin" : ""}`}/>
          {geocoding ? "Locating stops…" : (missing > 0 ? `Locate ${missing} address${missing === 1 ? "" : "es"}` : "Refresh")}
        </Button>
      </header>

      {loading ? (
        <p className="text-sm text-[#6B625B]">Loading…</p>
      ) : points.length === 0 ? (
        <div className="bg-white rounded-3xl border border-dashed border-[#E5DCCE] p-10 text-center text-[#6B625B]">
          <Navigation className="w-8 h-8 mx-auto mb-2 opacity-40"/>
          <p className="text-sm max-w-md mx-auto">
            {todays.length === 0
              ? "No stops today. Enjoy the quiet."
              : "Tap 'Locate addresses' — we'll geocode today's stops (about 1 second each) and drop them on the map."}
          </p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-3xl border border-[#E5DCCE] p-3 shadow-sm h-[520px] overflow-hidden">
            <MapContainer center={points[0]} zoom={12} scrollWheelZoom
              style={{ height: "100%", width: "100%", borderRadius: "1rem" }}>
              <TileLayer
                attribution='&copy; OpenStreetMap contributors'
                url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"/>
              <FitBounds points={points}/>
              {points.length > 1 && (
                <Polyline positions={points}
                  pathOptions={{ color: "#C85A17", weight: 5, opacity: 0.75, dashArray: "8 8" }}/>
              )}
              {todays.filter(a => a.latitude && a.longitude).map((a, i) => (
                <Marker key={a.id} position={[a.latitude, a.longitude]} icon={makeIcon(i + 1)}>
                  <Popup>
                    <div className="text-sm">
                      <div className="font-semibold">{a.client_name} · {a.pet_name}</div>
                      <div className="text-xs text-[#6B625B]">
                        {new Date(a.start).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · {a.service_name}
                      </div>
                      <div className="text-xs mt-1">{a.address}</div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>

          <div className="bg-white rounded-3xl border border-[#E5DCCE] p-5 shadow-sm">
            <h3 className="font-display text-lg font-bold mb-3 flex items-center gap-2">
              <Route className="w-4 h-4 text-[#C85A17]"/> Turn-by-turn
            </h3>
            <ol className="space-y-2" data-testid="turnbyturn">
              {todays.filter(a => a.latitude && a.longitude).map((a, i) => (
                <li key={a.id}>
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-[#C85A17] text-white grid place-content-center text-xs font-bold flex-shrink-0">
                      {i + 1}
                    </div>
                    <div className="text-sm flex-1 min-w-0">
                      <div className="font-semibold truncate">{a.client_name} · {a.pet_name}</div>
                      <div className="text-xs text-[#6B625B] truncate">{a.address}</div>
                    </div>
                  </div>
                  {segments[i] && (
                    <div className="ml-9 my-1 text-[10px] text-[#6B625B] pl-2 border-l-2 border-dashed border-[#E5DCCE] py-1">
                      → {segments[i].km.toFixed(1)} km · ~{segments[i].min} min
                    </div>
                  )}
                </li>
              ))}
            </ol>
            {missing > 0 && (
              <p className="text-[10px] text-amber-700 mt-3 bg-amber-50 border border-amber-200 rounded-lg p-2">
                {missing} stop{missing===1?"":"s"} couldn't be located from the address text.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
